import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import {
  Form,
  TextInput,
  Select,
  SelectItem,
  RadioButtonGroup,
  RadioButton,
  Accordion,
  AccordionItem,
  Button,
  Stack,
  ButtonSet,
  InlineLoading,
} from '@carbon/react';
import styles from './ict-service-form.scss';
import { closeWorkspace, OpenmrsDatePicker, showSnackbar, usePatient, useSession } from '@openmrs/esm-framework';
import { useTranslation } from 'react-i18next';
import type { ElicitedContactForm, IctInformationFormData, ICTFormProps } from '../types';
import {
  elicitedContactDefault,
  IctInformationConceptsUuid,
  ictInformationDefault,
  yesNoOptions,
  targetPopulation,
  eligibleForRecency,
  rtriResult,
  caseClassification,
  caseFindingStrategy,
  ifPict,
} from './utils/form-constant';
import dayjs from 'dayjs';
import { ICT_GENERAL_ENCOUNTER_TYPE, ictInformationFormUuid } from '../constants';
import { useLatestObs } from '../utils/data-table.resource';
import { defaultValuesFromEncounter } from './hooks/defaultValuesFromEncounter';
import { useFetchLocation } from './hooks/useFetchLocation';
import { createPerson, getLatestObsDate, getPersonData, relationshipPerson } from '../api/api';
import { createEncounter } from './hooks/createEncounter';
import { formatDateVal } from '../utils/utils';

const ICTgeneral: React.FC<ICTFormProps> = ({ encounter, type, mutateEncounters }) => {
  const patient = usePatient();
  const session = useSession();
  const today = new Date();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const encounterDate = new Date().toISOString();
  const birthDate = dayjs(patient?.patient?.birthDate);
  const currentAge = dayjs().diff(dayjs(patient?.patient?.birthDate), 'year');

  const { facilityInfo } = useFetchLocation();
  const [registrationDate, setRegistrationDate] = useState<Date | null>(null);
  const [hivConfirmeDate, setHivConfirmeDate] = useState<Date | null>(null);

  useEffect(() => {
    getLatestObsDate(
      patient.patientUuid,
      'dadc86a8-1e44-4a6d-91c2-e3b54089b54c',
      'a0fde61e-f493-4f1f-b709-22aa94f72dc3',
    ).then(setRegistrationDate);
  }, [patient.patientUuid]);

  useEffect(() => {
    getLatestObsDate(
      patient.patientUuid,
      '160753AAAAAAAAAAAAAAAAAAAAAAAAAAAAAA',
      '05add044-67f8-48c9-928d-79002ab19efe',
    ).then(setHivConfirmeDate);
  }, [patient.patientUuid]);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, touchedFields },
  } = useForm<IctInformationFormData>({
    defaultValues: encounter
      ? defaultValuesFromEncounter<IctInformationFormData>(
          encounter,
          ictInformationDefault,
          IctInformationConceptsUuid,
          [''],
        )
      : ictInformationDefault,
    mode: 'all',
  });

  const watchCrf = watch('crf');
  const watchRecency = watch('eligibleForRecency');
  const watchTestedForRecency = watch('testedForRecency');
  const watchHivst = watch('hivst');
  const watchCaseFinding = watch('caseFindingStrategy');
  const watchIfPict = watch('ifPict');

  useEffect(() => {
    // If user changes to "Not Eligible", clear test date
    if (watchRecency !== '8875cb57-a667-474d-9f9b-d3e592dfc75b') {
      setValue('testedForRecency', null, {
        shouldTouch: true,
      });
    }
    if (watchTestedForRecency !== '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA') {
      setValue('rtiTestDate', null, {
        shouldTouch: true,
      });
      setValue('rtriResult', null, {
        shouldTouch: true,
      });
    }
    if (watchCrf !== '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA') {
      setValue('crfCompletedDate', null, {
        shouldTouch: true,
      });
      setValue('cbsId', null, {
        shouldTouch: true,
      });
      setValue('initialCbsId', null, {
        shouldTouch: true,
      });
    }
    if (watchHivst !== '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA') {
      setValue('testedUsingHivst', null, {
        shouldTouch: true,
      });
      setValue('recommendedHivst', null, {
        shouldTouch: true,
      });
    }
    if (watchIfPict !== '5622AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA') {
      setValue('specifyPict', null, {
        shouldTouch: true,
      });
    }
    if (watchCaseFinding !== '9d0e9067-33a0-4d14-ad18-0baa4eab4297') {
      setValue('ifPict', null, {
        shouldTouch: true,
      });
    }
  }, [watchRecency, watchTestedForRecency, watchCrf, watchHivst, watchIfPict, watchCaseFinding, setValue]);

  const IctInformationFormData = useWatch({ control });

  const visibilityRules = useMemo(() => ({}), []);

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      await createEncounter<IctInformationFormData>(IctInformationFormData as IctInformationFormData, {
        namespace: 'rfe-forms',
        touchedFields,
        encounter,
        conceptsUuid: IctInformationConceptsUuid,
        encounterType: ICT_GENERAL_ENCOUNTER_TYPE,
        formUuid: ictInformationFormUuid,
        encounterDate,
        patientUuid: patient.patientUuid,
        providerUuid: session.currentProvider.uuid,
        locationUuid: facilityInfo.uuid,
        visibilityRules,
        context: { currentAge },
        encounterUuid: encounter?.uuid,
        mutateEncounters,
        workspaceName: 'index-case-information-workspace',
        editMode: type?.edit,
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false); // Reset submitting state
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const isFormAValid = await trigger();

    if (isFormAValid) {
      onSubmit();
    }
  };

  return (
    <Form onSubmit={handleFormSubmit} className={styles.formNew}>
      <h2 className={styles.heading}>INDEX CASE INFORMATION</h2>
      <div>
        <Stack gap={1} className={styles.container}>
          <Accordion>
            <AccordionItem className={styles.formContainer} title="GENERAL INFORMATION" open>
              <div className={styles.fieldWrapper}>
                <Controller
                  name="linkDate"
                  control={control}
                  rules={{
                    validate: (value) => {
                      let error = null;
                      if (!value) {
                        error = 'Linked Date is required';
                      }
                      const formattedValue = formatDateVal(value);
                      if (!formattedValue) {
                        error = error || 'Invalid date format';
                      }
                      return error || true;
                    },
                  }}
                  render={({ field }) => (
                    <OpenmrsDatePicker
                      id="linkDate"
                      labelText={
                        <span className={styles.label}>
                          {t('linkDate', 'Linked Date: ')}
                          <div className={styles.required}>*</div>
                        </span>
                      }
                      value={field.value}
                      minDate={registrationDate}
                      maxDate={today}
                      onChange={(date) => {
                        field.onChange(date);
                        field.onBlur();
                      }}
                      onBlur={() => field.onBlur()}
                      invalid={!!errors.linkDate}
                      invalidText={errors.linkDate?.message}
                      isReadOnly={type?.view}
                    />
                  )}
                />
              </div>

              <div className={styles.fieldWrapper}>
                <Controller
                  name="ictNumber"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      id="ictNumber"
                      readOnly={type?.view}
                      labelText={<span className={styles.label}>{t('ictNumber', 'ICT #')}</span>}
                      {...field}
                      invalid={!!errors.ictNumber}
                      invalidText={errors.ictNumber?.message}
                    />
                  )}
                />
              </div>
              <div className={styles.fieldWrapper}>
                <Controller
                  name="targetGroup"
                  control={control}
                  render={({ field }) => {
                    const filteredTargetPopulation =
                      patient?.patient?.gender === 'male'
                        ? targetPopulation.filter((item) => item.label !== 'WECSW')
                        : targetPopulation;

                    return (
                      <Select
                        className={styles.customSelect}
                        id="targetGroup"
                        labelText={<span className={styles.label}>{t('targetGroup', 'Target Population Group')}</span>}
                        {...field}
                      >
                        <SelectItem value="" text="Select" className={styles.customSelect} />
                        {filteredTargetPopulation.map((item) => (
                          <SelectItem
                            key={item.concept}
                            value={item.concept}
                            text={item.label}
                            className={styles.customSelect}
                          />
                        ))}
                      </Select>
                    );
                  }}
                />
              </div>
            </AccordionItem>
            <AccordionItem className={styles.formContainer} title="CBS INFORMATION" open>
              <div className={styles.fieldWrapper}>
                <Controller
                  name="linkedToCbs"
                  control={control}
                  render={({ field }) => (
                    <RadioButtonGroup
                      orientation="vertical"
                      id="linkedToCbs"
                      legendText={<span className={styles.label}>{t('linkedToCbs', 'Linked to CBS?')}</span>}
                      {...field}
                      onChange={(value: string) => field.onChange(value)}
                    >
                      {yesNoOptions.map((item) => (
                        <RadioButton
                          key={item.concept}
                          value={item.concept}
                          labelText={item.label}
                          checked={field.value === item.concept}
                        />
                      ))}
                    </RadioButtonGroup>
                  )}
                />
              </div>
              <div className={styles.fieldWrapper}>
                <Controller
                  name="crf"
                  control={control}
                  render={({ field }) => (
                    <RadioButtonGroup
                      orientation="vertical"
                      id="crf"
                      legendText={<span className={styles.label}>{t('crf', 'Is CRF completed?')}</span>}
                      {...field}
                      onChange={(value: string) => field.onChange(value)}
                    >
                      {yesNoOptions.map((item) => (
                        <RadioButton
                          key={item.concept}
                          value={item.concept}
                          labelText={item.label}
                          checked={field.value === item.concept}
                        />
                      ))}
                    </RadioButtonGroup>
                  )}
                />
              </div>
              <div className={styles.fieldWrapper}>
                {watchCrf === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                  <Controller
                    name="crfCompletedDate"
                    control={control}
                    render={({ field }) => (
                      <OpenmrsDatePicker
                        id="crfCompletedDate"
                        labelText={<span className={styles.label}>{t('crfCompletedDate', 'Date CRF completed:')}</span>}
                        value={field.value}
                        minDate={hivConfirmeDate || registrationDate}
                        maxDate={today}
                        onChange={(date) => {
                          field.onChange(date);
                          field.onBlur();
                        }}
                        onBlur={() => field.onBlur()}
                        invalid={!!errors.crfCompletedDate}
                        invalidText={errors.crfCompletedDate?.message}
                        isReadOnly={type?.view}
                      />
                    )}
                  />
                )}
              </div>
              <div className={styles.fieldWrapper}>
                {watchCrf === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                  <Controller
                    name="cbsId"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        id="cbsId"
                        labelText={<span className={styles.label}>{t('cbsId', 'CBS ID')}</span>}
                        {...field}
                      />
                    )}
                  />
                )}
              </div>

              <div className={styles.fieldWrapper}>
                {watchCrf === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                  <Controller
                    name="initialCbsId"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        id="initialCbsId"
                        labelText={<span className={styles.label}>{t('initialCbsId', 'Initial CBS ID')}</span>}
                        {...field}
                      />
                    )}
                  />
                )}
              </div>
            </AccordionItem>
            <AccordionItem className={styles.formContainer} title="RAPID TEST FOR RECENT INFECTION (RTRI)" open>
              <div className={styles.fieldWrapper}>
                <Controller
                  name="eligibleForRecency"
                  control={control}
                  render={({ field }) => (
                    <RadioButtonGroup
                      orientation="vertical"
                      id="eligibleForRecency"
                      legendText={<span className={styles.label}>{t('eligibleForRecency', 'Eligible for RTRI?')}</span>}
                      {...field}
                      onChange={(value: string) => field.onChange(value)}
                    >
                      {eligibleForRecency.map((item) => (
                        <RadioButton
                          key={item.concept}
                          value={item.concept}
                          labelText={item.label}
                          checked={field.value === item.concept}
                        />
                      ))}
                    </RadioButtonGroup>
                  )}
                />
              </div>

              <div className={styles.fieldWrapper}>
                {watchRecency === '8875cb57-a667-474d-9f9b-d3e592dfc75b' && (
                  <Controller
                    name="testedForRecency"
                    control={control}
                    render={({ field }) => (
                      <RadioButtonGroup
                        orientation="vertical"
                        id="testedForRecency"
                        legendText={<span className={styles.label}>{t('testedForRecency', 'Tested for recency')}</span>}
                        {...field}
                        onChange={(value: string) => field.onChange(value)}
                      >
                        {yesNoOptions.map((item) => (
                          <RadioButton
                            key={item.concept}
                            value={item.concept}
                            labelText={item.label}
                            checked={field.value === item.concept}
                          />
                        ))}
                      </RadioButtonGroup>
                    )}
                  />
                )}
              </div>

              <div className={styles.fieldWrapper}>
                {watchTestedForRecency === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                  <Controller
                    name="rtiTestDate"
                    control={control}
                    render={({ field }) => (
                      <OpenmrsDatePicker
                        id="rtiTestDate"
                        labelText={<span className={styles.label}>{t('rtiTestDate', 'RTRI test date:')}</span>}
                        value={field.value}
                        minDate={hivConfirmeDate || registrationDate}
                        maxDate={today}
                        onChange={(date) => {
                          field.onChange(date);
                          field.onBlur();
                        }}
                        onBlur={() => field.onBlur()}
                        invalid={!!errors.rtiTestDate}
                        invalidText={errors.rtiTestDate?.message}
                        isReadOnly={type?.view}
                      />
                    )}
                  />
                )}
              </div>
              <div className={styles.fieldWrapper}>
                {watchTestedForRecency === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                  <Controller
                    name="rtriResult"
                    control={control}
                    render={({ field }) => (
                      <RadioButtonGroup
                        orientation="vertical"
                        id="rtriResult"
                        legendText={<span className={styles.label}>{t('rtriResult', 'RTRI Result')}</span>}
                        {...field}
                        onChange={(value: string) => field.onChange(value)}
                      >
                        {rtriResult.map((item) => (
                          <RadioButton
                            key={item.concept}
                            value={item.concept}
                            labelText={item.label}
                            checked={field.value === item.concept}
                          />
                        ))}
                      </RadioButtonGroup>
                    )}
                  />
                )}
              </div>
            </AccordionItem>
            <AccordionItem className={styles.formContainer} title="HIV SELF TEST (HST) INFORMATION" open>
              <div className={styles.fieldWrapper}>
                <Controller
                  name="hivst"
                  control={control}
                  render={({ field }) => (
                    <RadioButtonGroup
                      orientation="vertical"
                      id="hivst"
                      legendText={<span className={styles.label}>{t('hivst', 'Do you know about HIVST?')}</span>}
                      {...field}
                      onChange={(value: string) => field.onChange(value)}
                    >
                      {yesNoOptions.map((item) => (
                        <RadioButton
                          key={item.concept}
                          value={item.concept}
                          labelText={item.label}
                          checked={field.value === item.concept}
                        />
                      ))}
                    </RadioButtonGroup>
                  )}
                />
              </div>
              <div className={styles.fieldWrapper}>
                {watchHivst === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                  <Controller
                    name="testedUsingHivst"
                    control={control}
                    render={({ field }) => (
                      <RadioButtonGroup
                        orientation="vertical"
                        id="testedUsingHivst"
                        legendText={
                          <span className={styles.label}>
                            {t('testedUsingHivst', 'Have you ever tested using HIVST?')}
                          </span>
                        }
                        {...field}
                        onChange={(value: string) => field.onChange(value)}
                      >
                        {yesNoOptions.map((item) => (
                          <RadioButton
                            key={item.concept}
                            value={item.concept}
                            labelText={item.label}
                            checked={field.value === item.concept}
                          />
                        ))}
                      </RadioButtonGroup>
                    )}
                  />
                )}
              </div>
              <div className={styles.fieldWrapper}>
                {watchHivst === '1065AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                  <Controller
                    name="recommendedHivst"
                    control={control}
                    render={({ field }) => (
                      <RadioButtonGroup
                        orientation="vertical"
                        id="recommendedHivst"
                        legendText={
                          <span className={styles.label}>
                            {t('recommendedHivst', 'Have you ever recommended your friends for HIVST?')}
                          </span>
                        }
                        {...field}
                        onChange={(value: string) => field.onChange(value)}
                      >
                        {yesNoOptions.map((item) => (
                          <RadioButton
                            key={item.concept}
                            value={item.concept}
                            labelText={item.label}
                            checked={field.value === item.concept}
                          />
                        ))}
                      </RadioButtonGroup>
                    )}
                  />
                )}
              </div>
            </AccordionItem>
            <AccordionItem className={styles.formContainer} title="CASE CLASSIFICATION" open>
              <div className={styles.fieldWrapper}>
                <Controller
                  name="caseClassification"
                  control={control}
                  render={({ field }) => (
                    <RadioButtonGroup
                      orientation="vertical"
                      id="caseClassification"
                      legendText={
                        <span className={styles.label}>{t('caseClassification', 'Case Classification Status')}</span>
                      }
                      {...field}
                      onChange={(value: string) => field.onChange(value)}
                    >
                      {caseClassification.map((item) => (
                        <RadioButton
                          key={item.concept}
                          value={item.concept}
                          labelText={item.label}
                          checked={field.value === item.concept}
                        />
                      ))}
                    </RadioButtonGroup>
                  )}
                />
              </div>
              <div className={styles.fieldWrapper}>
                <Controller
                  name="caseFindingStrategy"
                  control={control}
                  render={({ field }) => (
                    <RadioButtonGroup
                      orientation="vertical"
                      id="caseFindingStrategy"
                      legendText={
                        <span className={styles.label}>{t('caseFindingStrategy', 'Case finding strategy')}</span>
                      }
                      {...field}
                      onChange={(value: string) => field.onChange(value)}
                    >
                      {caseFindingStrategy.map((item) => (
                        <RadioButton
                          key={item.concept}
                          value={item.concept}
                          labelText={item.label}
                          checked={field.value === item.concept}
                        />
                      ))}
                    </RadioButtonGroup>
                  )}
                />
              </div>

              <div className={styles.fieldWrapper}>
                {watchCaseFinding === '9d0e9067-33a0-4d14-ad18-0baa4eab4297' && (
                  <Controller
                    name="ifPict"
                    control={control}
                    render={({ field }) => (
                      <Select
                        id="ifPict"
                        labelText={<span className={styles.label}>{t('ifPict', 'PICT service delivery point')}</span>}
                        {...field}
                      >
                        <SelectItem value="" text="Select" />
                        {ifPict.map((item) => (
                          <SelectItem key={item.concept} value={item.concept} text={item.label} />
                        ))}
                      </Select>
                    )}
                  />
                )}
              </div>

              <div className={styles.fieldWrapper}>
                <div className={styles.lastField}>
                  {watchIfPict === '5622AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA' && (
                    <Controller
                      name="specifyPict"
                      control={control}
                      rules={{
                        maxLength: {
                          value: 50,
                          message: 'Please use a maximum of 50 characters',
                        },
                      }}
                      render={({ field }) => (
                        <TextInput
                          id="specifyPict"
                          labelText={
                            <span className={styles.label}>
                              {t('specifyPict', 'If other PICT service delivery point')}
                            </span>
                          }
                          {...field}
                        />
                      )}
                    />
                  )}
                </div>
              </div>
            </AccordionItem>
          </Accordion>
        </Stack>
      </div>
      <ButtonSet className={styles.buttonSet}>
        <Button
          onClick={() => closeWorkspace('index-case-information-workspace')}
          style={{ maxWidth: 'none', width: '50%' }}
          className={styles.button}
          kind="secondary"
        >
          {t('discard', 'Discard')}
        </Button>
        <Button
          style={{ maxWidth: 'none', width: '50%' }}
          className={styles.button}
          kind="primary"
          type="submit"
          disabled={type?.view || isSubmitting}
        >
          {isSubmitting ? <InlineLoading description="submitting..." /> : type?.edit ? 'Update' : 'Save'}
        </Button>
      </ButtonSet>
    </Form>
  );
};

export default ICTgeneral;
